/**
 * Pure mapping from an ordered `PinnedObjectRef[]` to an ordered array of
 * `Pinned*` wrapper tree nodes, ready to be prepended to a connection node's
 * children.
 *
 * - Synchronous and side-effect-free.
 * - Preserves input order (the store owns the ordering; this module only maps).
 * - When `queryClient` is provided, each node's `resolution` is computed from
 *   the live TanStack Query cache via {@link resolvePin}. When omitted, each
 *   node keeps the factory default (`resolution: 'unresolved'`) — this is the
 *   correct state for callers that have no cache yet (e.g. fresh tree
 *   construction before any database listing has been fetched).
 */

import type { QueryClient } from '@tanstack/react-query';
import type { DatabaseExplorerNode } from '../../../explorer/models/nodeTypes';
import type { PinnedObjectRef } from '../../../../types/electron';
import {
  createPinnedDatabaseNode,
  createPinnedSchemaNode,
  createPinnedFolderNode,
  createPinnedObjectNode,
  createPinnedWorkspaceNode,
} from '../../../explorer/models/nodeFactories';
import { createPinnedStorageNode } from '../../../explorer/models/azureStorageNodeFactories';
// `isWorkspacePinRef` is a runtime value; it lives in `pinRef.ts` (the renderer
// PinnedObjectRef-utility module) rather than being imported from the electron
// composite project, which would break the `tsc` build — see the note there.
import { isWorkspacePinRef } from './pinRef';
import { resolvePin } from './pinResolution';

export function buildPinnedChildren(args: {
  connectionId: string;
  parentId: string;
  pins: PinnedObjectRef[];
  engine: string;
  queryClient?: QueryClient;
}): DatabaseExplorerNode[] {
  const { connectionId, parentId, pins, engine, queryClient } = args;
  return pins.map((ref): DatabaseExplorerNode => {
    let node: DatabaseExplorerNode;
    // F7 — workspace pins (tableView/query/notebook) render as self-contained
    // `pinned-workspace` leaf nodes. They carry their own payload, have no
    // parent listing to classify, and are NEVER routed through `resolvePin`
    // (their resolution is always 'resolved'), so return the node directly
    // BEFORE the object-assuming branches / the query-cache stamp below.
    if (isWorkspacePinRef(ref)) {
      return createPinnedWorkspaceNode({ connectionId, parentId, ref, engine });
    }
    if (ref.kind === 'storage') {
      // Azure Storage (spec §8.3): typed wrapper, resolved from the shared
      // `['azure-storage', …]` listing cache below.
      node = createPinnedStorageNode({ connectionId, parentId, ref });
    } else if (ref.kind === 'database') {
      node = createPinnedDatabaseNode({
        connectionId,
        parentId,
        ref,
        metadata: { connectionId, name: ref.database, engine },
      });
    } else if (ref.kind === 'schema') {
      node = createPinnedSchemaNode({
        connectionId,
        parentId,
        ref,
        metadata: {
          connectionId,
          database: ref.database,
          schema: ref.schema,
          // `objectCount` is part of the canonical metadata; we don't have a
          // live count when rehydrating from disk, so seed 0. The cache
          // subscriber re-stamps `resolution` later — the count is purely
          // cosmetic (shown in the row's badge for canonical schema
          // containers) and not load-bearing for any logic.
          objectCount: 0,
          engine,
        },
      });
    } else if (ref.kind === 'folder') {
      node = createPinnedFolderNode({
        connectionId,
        parentId,
        ref,
        metadata: { connectionId, database: ref.database, engine },
      });
    } else {
      // ref.kind === 'object'
      node = createPinnedObjectNode({
        connectionId,
        parentId,
        ref,
        metadata: { connectionId, database: ref.database, engine },
      });
    }

    if (queryClient) {
      const resolution = resolvePin(ref, queryClient, connectionId);
      return { ...node, resolution } as DatabaseExplorerNode;
    }
    return node;
  });
}
